import { Injectable } from "@angular/core";
import { AccountsService } from "../accounts.service";

/**
 * Legacy + new (`section.*.view` / `section.*.edit`) permission rules per section base key.
 * Extend this registry when migrating more areas from flat tokens.
 */
export interface SectionPermissionRule {
  view: {
    /** New API keys that grant read access (any match). */
    newKeys: string[];
    /** Legacy tokens: any single match grants view (e.g. CALENDAR). */
    legacyAnyOf?: string[];
  };
  edit: {
    /** New API keys that grant write access. */
    newKeys: string[];
    /**
     * Legacy: OR of groups; each group is AND of tokens.
     * Matches reservation-detail default_permission style: [["WRITE","CALENDAR"],["WINERY_ADMIN"]].
     */
    legacyAnyOfGroups?: string[][];
  };
}

export const SECTION_PERMISSION_REGISTRY: Record<string, SectionPermissionRule> = {
  /** Calendar / reservations (list + reservation detail edit). */
  "section.calendar": {
    view: {
      newKeys: ["section.calendar.view", "section.calendar.edit"],
      legacyAnyOf: ["CALENDAR"],
    },
    edit: {
      newKeys: ["section.calendar.edit"],
      legacyAnyOfGroups: [
        ["WRITE", "CALENDAR"],
        ["WINERY_ADMIN"],
      ],
    },
  },
  "section.reports_dashboard": {
    view: {
      newKeys: [
        "section.reports_dashboard.view",
        "section.reports_dashboard.edit",
      ],
      legacyAnyOf: ["DASHBOARD"],
    },
    edit: {
      newKeys: [
        "section.reports_dashboard.edit",
        "section.dashboard.export.edit",
        "section.reports_dashboard.export.edit",
      ],
      legacyAnyOfGroups: [
        ["DASHBOARD"],
        ["WRITE", "DASHBOARD"],
        ["WINERY_ADMIN"],
      ],
    },
  },
  "section.availability": {
    view: {
      newKeys: ["section.availability.view", "section.availability.edit"],
    },
    edit: {
      newKeys: ["section.availability.edit"],
      legacyAnyOfGroups: [
        ["WRITE", "CALENDAR"],
        ["WINERY_ADMIN"],
      ],
    },
  },
  /** CRM / Sales: legacy flat tokens stay valid alongside `section.crm.*` / `section.sales.*`. */
  "section.crm": {
    view: {
      newKeys: ["section.crm.view", "section.crm.edit"],
      legacyAnyOf: ["CRM"],
    },
    edit: {
      newKeys: ["section.crm.edit"],
      /** Raw JWT only (bridge does not apply here). Include lone legacy CRM for pre–section tokens. */
      legacyAnyOfGroups: [
        ["CRM"],
        ["WRITE", "CRM"],
        ["WINERY_ADMIN"],
      ],
    },
  },
  "section.sales": {
    view: {
      newKeys: ["section.sales.view", "section.sales.edit"],
      legacyAnyOf: ["ORDERS"],
    },
    edit: {
      newKeys: ["section.sales.edit"],
      legacyAnyOfGroups: [
        ["ORDERS"],
        ["WRITE", "ORDERS"],
        ["WINERY_ADMIN"],
      ],
    },
  },
  /** Embedded POS in Wine Suite web app (`/pos`, `/pos-terminal`). Not the detached wspos app. */
  "section.point_of_sale.general": {
    view: {
      newKeys: [
        "section.point_of_sale.general.view",
        "section.point_of_sale.general.edit",
      ],
    },
    edit: {
      newKeys: ["section.point_of_sale.general.edit"],
      legacyAnyOfGroups: [
        ["WINERY_ADMIN"],
      ],
    },
  },
  "section.ws_payments.general": {
    view: {
      newKeys: [
        "section.ws_payments.general.view",
        "section.ws_payments.general.edit",
      ],
      legacyAnyOf: ["WINERY_ADMIN"],
    },
    edit: {
      newKeys: ["section.ws_payments.general.edit"],
      legacyAnyOfGroups: [["WINERY_ADMIN"]],
    },
  },
  "section.ws_fiscality.general": {
    view: {
      newKeys: [
        "section.ws_fiscality.general.view",
        "section.ws_fiscality.general.edit",
      ],
      legacyAnyOf: ["ORDERS", "WINERY_ADMIN"],
    },
    edit: {
      newKeys: ["section.ws_fiscality.general.edit"],
      legacyAnyOfGroups: [
        ["ORDERS"],
        ["WRITE", "ORDERS"],
        ["WINERY_ADMIN"],
      ],
    },
  },
  "section.settings": {
    view: {
      newKeys: ["section.settings.view", "section.settings.edit"],
    },
    edit: {
      newKeys: ["section.settings.edit"],
      legacyAnyOfGroups: [["WINERY_ADMIN"]],
    },
  },
  "section.homepage": {
    view: {
      newKeys: ["section.homepage.view", "section.homepage.edit"],
    },
    edit: {
      newKeys: ["section.homepage.edit"],
      legacyAnyOfGroups: [["WINERY_ADMIN"]],
    },
  },
};

@Injectable({
  providedIn: "root",
})
export class PermissionResolverService {
  constructor(private readonly accounts: AccountsService) {}

  /**
   * User may open the section (read). Edit implies view.
   */
  canViewSection(sectionBase: string): boolean {
    if (this.canEditSection(sectionBase)) {
      return true;
    }
    const rule = SECTION_PERMISSION_REGISTRY[sectionBase];
    if (rule) {
      if (rule.view.newKeys.some((k) => this.accounts.hasPermission(k))) {
        return true;
      }
      if (rule.view.legacyAnyOf?.some((k) => this.accounts.hasPermission(k))) {
        return true;
      }
      return false;
    }
    return (
      this.accounts.hasPermission(`${sectionBase}.view`) ||
      this.accounts.hasPermission(`${sectionBase}.edit`)
    );
  }

  /**
   * User may change data in the section (write).
   */
  canEditSection(sectionBase: string): boolean {
    const rule = SECTION_PERMISSION_REGISTRY[sectionBase];
    if (rule) {
      if (rule.edit.newKeys.some((k) => this.accounts.hasPermission(k))) {
        return true;
      }
      if (rule.edit.legacyAnyOfGroups?.length) {
        const legacyOk = rule.edit.legacyAnyOfGroups.some((group) =>
          group.every((k) => this.accounts.hasRawPermission(k))
        );
        if (legacyOk) {
          return true;
        }
      }
      if (this.hasMacroGranularEdit(sectionBase)) {
        return true;
      }
      return false;
    }
    if (this.hasMacroGranularEdit(sectionBase)) {
      return true;
    }
    return this.accounts.hasPermission(`${sectionBase}.edit`);
  }

  /**
   * Macro section edit: any `section.<area>.<sub>.edit` under the macro base counts.
   */
  private hasMacroGranularEdit(sectionBase: string): boolean {
    const macro = sectionBase.replace(/^\s+|\s+$/g, "");
    const esc = macro.replace(/\./g, "\\.");
    const re = new RegExp(`^${esc}\\.[^.]+\\.edit$`, "i");
    return this.accounts.getCachedPermissions().some((p) => re.test(p));
  }
}
